import moment from "moment";

export const MAX_INSTALLMENTS = 6;

const DATE_FORMAT = "YYYY-MM-DD";

const clampCount = (n) =>
  Math.min(MAX_INSTALLMENTS, Math.max(1, Math.round(Number(n) || 1)));

/**
 * Divide um total em centavos em `count` parcelas inteiras. O resto da
 * divisão vai pras primeiras parcelas (ex: 10000 / 3 → 3334, 3333, 3333),
 * então a soma sempre bate com o total.
 */
export function splitAmount(totalCents, count) {
  const n = clampCount(count);
  const total = Math.max(0, Math.round(Number(totalCents) || 0));
  const base = Math.floor(total / n);
  const remainder = total - base * n;
  return Array.from({ length: n }, (_, i) => base + (i < remainder ? 1 : 0));
}

/** Data de vencimento da parcela `index` (0 = primeira), mês a mês. */
export function dueDateFor(firstDueDate, index) {
  return moment(firstDueDate, DATE_FORMAT)
    .add(index, "months")
    .format(DATE_FORMAT);
}

/**
 * Monta as parcelas iniciais do modal a partir do pedido. Se o pedido já tem
 * `paymentSchedule` salvo, usa ele; senão gera `installments` parcelas a
 * partir da data da venda. Pedido já pago sem parcelas → tudo marcado pago.
 */
export function buildInitialSchedule(order) {
  if (Array.isArray(order?.paymentSchedule) && order.paymentSchedule.length) {
    return order.paymentSchedule.map((p, i) => ({
      number: i + 1,
      amount: p.amount,
      dueDate: moment(p.dueDate).format(DATE_FORMAT),
      paid: Boolean(p.paid),
      paidAt: p.paid && p.paidAt ? moment(p.paidAt).format(DATE_FORMAT) : "",
    }));
  }

  const firstDueDate = moment(order?.dataTransaction || undefined).format(
    DATE_FORMAT,
  );
  const alreadyPaid = order?.status === "paid";

  return splitAmount(order?.amount, order?.installments).map((amount, i) => ({
    number: i + 1,
    amount,
    dueDate: dueDateFor(firstDueDate, i),
    paid: alreadyPaid,
    paidAt: alreadyPaid ? firstDueDate : "",
  }));
}

/**
 * Muda a quantidade de parcelas: redivide o valor, mantém vencimento e
 * "pago" das parcelas que já existiam e gera as novas mês a mês depois
 * da primeira.
 */
export function resizeSchedule(schedule, count, totalCents) {
  const amounts = splitAmount(totalCents, count);
  const firstDueDate = schedule[0]?.dueDate || moment().format(DATE_FORMAT);

  return amounts.map((amount, i) => {
    const existing = schedule[i];
    return {
      number: i + 1,
      amount,
      dueDate: existing?.dueDate || dueDateFor(firstDueDate, i),
      paid: existing?.paid || false,
      paidAt: existing?.paidAt || "",
    };
  });
}

/** Marca/desmarca uma parcela como paga (hoje como data padrão). */
export function togglePaid(
  schedule,
  index,
  today = moment().format(DATE_FORMAT),
) {
  return schedule.map((p, i) => {
    if (i !== index) return p;
    const paid = !p.paid;
    return { ...p, paid, paidAt: paid ? p.paidAt || today : "" };
  });
}

export function updateInstallment(schedule, index, field, value) {
  return schedule.map((p, i) => (i === index ? { ...p, [field]: value } : p));
}

export function isFullyPaid(schedule) {
  return schedule.length > 0 && schedule.every((p) => p.paid);
}

/**
 * Formato enviado pro back. Datas vão com meio-dia pra não "voltarem" um
 * dia por causa do fuso (UTC no servidor × BRT aqui).
 */
export function toPayload(schedule) {
  return schedule.map((p) => ({
    number: p.number,
    amount: p.amount,
    dueDate: `${p.dueDate}T12:00:00`,
    paid: p.paid,
    paidAt: p.paid && p.paidAt ? `${p.paidAt}T12:00:00` : null,
  }));
}
